/**
 * Botones inteligentes (la marca registrada de la UX de Odoo).
 *
 * Arriba del formulario de un registro, una fila de tarjetas-botón con un
 * CONTADOR y una etiqueta: "3 Facturas", "2 Ventas", "1 Suscripción".
 *
 * Como todo en el renderer, no sabe nada de ningún módulo: dibuja lo que el
 * backend adjunta al detalle del registro (`smart_buttons`), con el conteo ya
 * evaluado contra los permisos y las reglas del usuario. La UI no cuenta ni decide
 * qué botón mostrar; sólo obedece.
 *
 * Cuatro decisiones de comportamiento:
 *
 * 1. **Abre un cuadro encima de la ficha, no navega.** El pedido de Plena:
 *    «que puedas tocar ahí y que no te lleve a otra sección; te muestre en un
 *    subcuadro» lo que cuenta la tarjeta, en todas las fichas. Una tarjeta con
 *    `accion` abre el cuadro propio de esa acción (lo vendido, abierto por
 *    administrador o por consorcio); las demás, el cuadro genérico con las
 *    filas que cuenta (`api.smartButtonTable`): las mismas, ni una más.
 * 2. **La lista completa queda a un clic.** El cuadro trae «Abrir la lista
 *    completa», que lleva a `/m/<destino>` con el dominio de la tarjeta como
 *    filtro base: ahí se pagina, se filtra y se exporta.
 * 3. **Si el cuadro no se puede abrir, navega como antes.** Si la acción falla
 *    se prueba primero el cuadro genérico; si tampoco, se va a la lista —por la
 *    guardia de cambios sin guardar de la ficha (`onNavegar`)—: un botón que no
 *    hace nada es peor que uno que hace lo de antes.
 * 4. **Contador cero se abre igual, apagado.** «0 Facturas» atenuada pero
 *    tocable: un cuadro vacío también es una respuesta.
 */

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { ICONOS_DE_MENU, IconLayers } from '@/components/shell/icons';
import { filteredModelPath } from '@/components/shell/menu-utils';
import { api } from '@/lib/api';
import type { SmartButton, TabularReportData } from '@/lib/types';
import { leerCuadro, leerReporteTabular, TabularReportDialog } from '@/views/TabularReport';

export interface SmartButtonsProps {
  buttons: SmartButton[];
  /** Modelo y registro abiertos: hacen falta para pedir el cuadro de una tarjeta. */
  model?: string;
  recordId?: number | undefined;
  /**
   * Cómo ir a la lista cuando el cuadro no se puede abrir. La ficha pasa su
   * guardia de cambios sin guardar (`useDirtyGuard().navegarConAviso`): un
   * `<button>` no es un enlace y la guardia sólo intercepta enlaces, así que sin
   * esto se salía de la ficha perdiendo lo que se estaba editando.
   */
  onNavegar?: (destino: string) => void;
}

interface Panel {
  titulo: string;
  datos: TabularReportData;
  /** La lista filtrada de la tarjeta, para «Abrir la lista completa». */
  destino: string;
}

export function SmartButtons({ buttons, model, recordId, onNavegar }: SmartButtonsProps) {
  // El cuadro que abrió una tarjeta. Vive ACÁ y no en cada tarjeta para que
  // abrir uno cierre el anterior: dos cuadros encimados serían dos verdades
  // discutiendo.
  const [panel, setPanel] = useState<Panel | null>(null);

  if (buttons.length === 0) return null;

  return (
    <>
      <div className="mb-4 flex flex-wrap gap-2" role="group" aria-label="Registros relacionados">
        {buttons.map((boton) => (
          <SmartButtonCard
            key={boton.name}
            button={boton}
            {...(model === undefined ? {} : { model })}
            {...(recordId === undefined ? {} : { recordId })}
            {...(onNavegar === undefined ? {} : { onNavegar })}
            onPanel={setPanel}
          />
        ))}
      </div>
      <TabularReportDialog
        titulo={panel?.titulo ?? ''}
        datos={panel?.datos ?? null}
        acciones={
          panel === null ? undefined : (
            <Link
              to={panel.destino}
              className="text-sm font-medium text-accent hover:underline"
              onClick={() => {
                setPanel(null);
              }}
            >
              Abrir la lista completa
            </Link>
          )
        }
        onClose={() => {
          setPanel(null);
        }}
      />
    </>
  );
}

function SmartButtonCard({
  button,
  model,
  recordId,
  onNavegar,
  onPanel,
}: {
  button: SmartButton;
  model?: string;
  recordId?: number;
  onNavegar?: (destino: string) => void;
  onPanel: (panel: Panel | null) => void;
}) {
  const Icono = button.icon !== null ? (ICONOS_DE_MENU[button.icon] ?? IconLayers) : IconLayers;
  const vacio = button.count === 0;
  const destino = filteredModelPath(button.target.model, button.target.domain);
  const [corriendo, setCorriendo] = useState(false);
  const navigate = useNavigate();
  const irALaLista = (): void => {
    if (onNavegar !== undefined) onNavegar(destino);
    else navigate(destino);
  };

  const clases = [
    'group inline-flex min-w-[7.5rem] items-center gap-3 rounded-xl border border-line bg-surface px-4 py-2.5',
    'transition-colors hover:border-accent hover:bg-accent/5',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
    vacio ? 'opacity-60' : '',
  ].join(' ');

  const cuerpo = (
    <>
      <Icono aria-hidden="true" className="h-5 w-5 shrink-0 text-fg-muted group-hover:text-accent" />
      <span className="flex flex-col leading-tight">
        <span className="text-lg font-semibold tabular-nums text-fg">{button.count}</span>
        <span className="text-xs font-medium uppercase tracking-wide text-fg-muted">
          {button.label}
        </span>
      </span>
    </>
  );
  const etiqueta = `${button.label}: ${button.count} ${button.count === 1 ? 'registro' : 'registros'}`;

  // Sin el registro abierto no hay de qué pedir el cuadro (no pasa en una ficha
  // guardada): la tarjeta navega, que es lo que hacía siempre.
  if (model === undefined || recordId === undefined) {
    return (
      <Link to={destino} aria-label={etiqueta} className={clases}>
        {cuerpo}
      </Link>
    );
  }

  const conAccion = button.accion != null && button.accion !== '';

  // Un `<button>` de verdad y no un enlace disfrazado: no lleva a otra URL, así
  // que ofrecer «abrir en otra pestaña» sería mentir. La lista completa está
  // dentro del cuadro.
  return (
    <button
      type="button"
      disabled={corriendo}
      aria-label={etiqueta}
      onClick={() => {
        setCorriendo(true);
        const generico = (): Promise<TabularReportData | null> =>
          api.smartButtonTable(model, recordId, button.name).then((respuesta) => leerCuadro(respuesta.table));
        // Con acción, su cuadro; si la acción falla o no trae cuadro (no es
        // para este registro, no hay permiso para correrla), el cuadro
        // genérico de la tarjeta, que muestra lo mismo que cuenta.
        const pedido: Promise<TabularReportData | null> = conAccion
          ? api
              .callAction(model, recordId, button.accion as string, {})
              .then((resultado) => leerReporteTabular(resultado))
              .catch(() => null)
              .then((cuadro) => cuadro ?? generico())
          : generico();
        void pedido
          .then((cuadro) => {
            if (cuadro !== null) onPanel({ titulo: button.label, datos: cuadro, destino });
            else irALaLista();
          })
          // Si tampoco hay cuadro genérico (el servidor no tiene el pedido, como
          // la demo sin conexión), la tarjeta hace lo de siempre: ir a la lista.
          // Antes el clic no hacía nada y no decía nada.
          .catch(() => {
            irALaLista();
          })
          .finally(() => {
            setCorriendo(false);
          });
      }}
      className={clases}
    >
      {cuerpo}
    </button>
  );
}
